import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthOrgs } from '../hooks/useAuthOrgs';

interface AccountMenuProps {
  onLogout: () => void;
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export function AccountMenu({ onLogout }: AccountMenuProps) {
  const { user } = useAuthContext();
  const { orgs, current, hasMultiple, switchOrg } = useAuthOrgs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!user || !current) {
    return null;
  }

  const initial = (user.name?.[0] || user.email?.[0] || '?').toUpperCase();

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        data-tour="account-menu"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account menu, ${user.email}`}
        title={user.email}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-0.5 md:gap-1.5 rounded-full border border-gray-200 bg-white pl-0.5 pr-1 py-0.5 md:pl-1 md:pr-2 md:py-1 hover:bg-gray-50 transition-colors"
      >
        <span className="w-7 h-7 text-xs md:w-9 md:h-9 md:text-sm rounded-full bg-green-100 text-green-800 font-bold flex items-center justify-center">
          {initial}
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-[60] py-1 overflow-hidden"
          role="menu"
          data-tour="account-menu-panel"
        >
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">{user.name || user.email}</p>
            {user.name ? <p className="text-xs text-gray-500 truncate">{user.email}</p> : null}
            <p className="text-xs text-gray-600 mt-2">
              Workspace:{' '}
              <span className="font-medium text-gray-800" data-tour="account-menu-workspace">
                {current.orgName}
              </span>
            </p>
          </div>

          {hasMultiple ? (
            <div className="py-1 border-b border-gray-100" data-tour="org-switcher">
              <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Switch workspace
              </p>
              {orgs.map((org) => (
                <button
                  key={org.orgId}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    if (!org.isCurrent) {
                      void switchOrg(org.orgId);
                    }
                    setOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between gap-2 ${
                    org.isCurrent ? 'bg-green-50 text-green-800' : 'text-gray-700'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="font-medium block truncate">{org.orgName}</span>
                    <span className="text-xs text-gray-400 block truncate">{org.orgDomain}</span>
                  </span>
                  {org.isCurrent ? (
                    <svg
                      className="w-4 h-4 text-green-600 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}

          <Link
            to="/profile"
            role="menuitem"
            className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
            onClick={() => setOpen(false)}
          >
            Profile
          </Link>
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-4 py-3 text-sm text-red-700 hover:bg-red-50 border-t border-gray-100"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
